import NotificationPopupModal from './NotificationPopupModal'
import useNotifications, { NotificationsInputProps } from './useNotifications'

import { useCallback, useRef } from 'react'

import {
  Badge,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Tooltip,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { AiOutlineGift } from 'react-icons/ai'
import { BiBell, BiInfoCircle } from 'react-icons/bi'
import { FaCar } from 'react-icons/fa'
import { IoPodium, IoTrophyOutline } from 'react-icons/io5'

export default function NotificationsView({
  tooltip,
}: NotificationsInputProps) {
  const [t] = useTranslation('layout')
  const {
    handleClick,
    open,
    anchorEl,
    handleClose,
    notifications,
    unreadCount,
    loading,
    handleNotificationClick,
    markAllAsRead,
    hasMore,
    loadMore,
    loadingMore,
    popupModalOpen,
    currentPopupNotification,
    handlePopupModalClose,
  } = useNotifications({ tooltip })

  // Reference to the notification list container for scroll detection
  const listRef = useRef<HTMLDivElement>(null)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return format(date, 'dd MMM yyyy, HH:mm', { locale: es })
  }

  // Function to get the appropriate icon based on notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'CHALLENGE':
        return <IoTrophyOutline size={30} />
      case 'REWARD':
        return <AiOutlineGift size={30} />
      case 'CARPOOLING':
        return <FaCar size={30} />
      case 'ACTIVITY':
        return <IoPodium size={30} />
      case 'GENERAL':
      default:
        return <BiInfoCircle size={30} />
    }
  }

  // Handle scroll event to implement infinite scroll
  const handleScroll = useCallback(() => {
    if (!listRef.current || loadingMore || !hasMore) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    // Load more when user scrolls to bottom (with a small threshold)
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      loadMore()
    }
  }, [loadingMore, hasMore, loadMore])

  return (
    <>
      {/* Popup Notification Modal */}
      <NotificationPopupModal
        open={popupModalOpen}
        notification={currentPopupNotification}
        onClose={handlePopupModalClose}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title={tooltip}>
          <IconButton
            onClick={handleClick}
            size='small'
            sx={{ ml: 2 }}
            aria-controls={open ? 'notification-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
          >
            <Badge badgeContent={unreadCount} color='error'>
              <BiBell size={24} />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id='notification-menu'
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            width: 360,
            maxHeight: 450,
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#8d949a' }}
          >
            {t('header.notifications.title')}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size='small'
              onClick={markAllAsRead}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: '#00c8b4',
                textDecoration: 'underline',
              }}
            >
              {t('header.notifications.markAllAsRead')}
            </Button>
          )}
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              {t('header.notifications.empty')}
            </Typography>
          </Box>
        ) : (
          <Box
            ref={listRef}
            sx={{ maxHeight: 350, overflow: 'auto' }}
            onScroll={handleScroll}
          >
            <List sx={{ p: 0 }}>
              {notifications.map(notification => (
                <ListItem
                  key={notification.uuid}
                  disablePadding
                  sx={{
                    backgroundColor:
                      notification.status === 'READ'
                        ? 'transparent'
                        : '#F2F7FA',
                  }}
                >
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{ padding: 0, borderBottom: '1.5px solid #E9F0F3' }}
                  >
                    <ListItemIcon
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={notification.title}
                      secondary={
                        <>
                          <Typography
                            component='span'
                            variant='body2'
                            sx={{
                              display: 'block',
                              fontSize: '0.8rem',
                              color: '#3C3C3B',
                            }}
                          >
                            {notification.description}
                          </Typography>
                          <Typography
                            component='span'
                            variant='caption'
                            sx={{ color: '#8D949A' }}
                          >
                            {formatDate(notification.created_at)}
                          </Typography>
                        </>
                      }
                      primaryTypographyProps={{
                        fontWeight:
                          notification.status === 'READ' ? 'medium' : 'medium',
                        fontSize: '0.9rem',
                        color: '#3C3C3B',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {loadingMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </List>
          </Box>
        )}
      </Menu>
    </>
  )
}
